import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  IconButton,
  Backdrop,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";
import { Edit, Add, Save, Delete } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  createTimeSlot,
  deleteTimeSlot,
  getTimeSlot,
  updateTimeSlot,
} from "../../redux/action/rate";

interface TimeSlot {
  id?: number;
  window: string;
}

const TimeSlots: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  const [startHour, setStartHour] = useState("");
  const [startMinute, setStartMinute] = useState("");
  const [startAmPm, setStartAmPm] = useState("AM");
  const [endHour, setEndHour] = useState("");
  const [endMinute, setEndMinute] = useState("");
  const [endAmPm, setEndAmPm] = useState("PM");

  const [editStartHour, setEditStartHour] = useState("");
  const [editStartMinute, setEditStartMinute] = useState("");
  const [editStartAmPm, setEditStartAmPm] = useState("AM");
  const [editEndHour, setEditEndHour] = useState("");
  const [editEndMinute, setEditEndMinute] = useState("");
  const [editEndAmPm, setEditEndAmPm] = useState("PM");

  useEffect(() => {
    fetchTimeSlots();
  }, []);

  const fetchTimeSlots = () => {
    const carrierId = localStorage.getItem("CarrierId");
    if (carrierId) {
      const carrierIdNumber = parseInt(carrierId, 10);
      if (!isNaN(carrierIdNumber)) {
        dispatch<any>(getTimeSlot(carrierIdNumber))
          .then(unwrapResult)
          .then((res: TimeSlot[]) => {
            setTimeSlots(res || []);
          })
          .catch((err: any) => {
            setLoadingMessage("Failed to fetch time slots data.");
            console.error("Error fetching time slots:", err);
          });
      }
    }
  };

  const formatTimeSlot = (hour: string, minute: string, amPm: string) => {
    const formattedHour = hour.padStart(2, "0");
    const formattedMinute = minute.padStart(2, "0");
    return `${formattedHour}:${formattedMinute} ${amPm}`;
  };

  const validateTimeInputs = (hour: string, minute: string, amPm: string): boolean => {
    if (!hour || !minute || !amPm) return false;
    if (isNaN(Number(hour))) return false;
    if (isNaN(Number(minute))) return false;
    if (Number(hour) < 1 || Number(hour) > 12) return false;
    if (Number(minute) < 0 || Number(minute) > 59) return false;
    return true;
  };

  const handleAddTimeSlot = async () => {
    if (
      !validateTimeInputs(startHour, startMinute, startAmPm) ||
      !validateTimeInputs(endHour, endMinute, endAmPm)
    ) {
      setLoadingMessage("Please enter valid time values");
      setIsLoading(true);
      setTimeout(() => setIsLoading(false), 2000);
      return;
    }

    const startTime = formatTimeSlot(startHour, startMinute, startAmPm);
    const endTime = formatTimeSlot(endHour, endMinute, endAmPm);
    const timeSlotString = `${startTime} - ${endTime}`;

    setIsLoading(true);
    setLoadingMessage("Adding time slot...");
    
    try {
      const response = await dispatch<any>(createTimeSlot({
        window: timeSlotString,
        carrier_id: parseInt(localStorage.getItem("CarrierId") || "0", 10),
      }));
      const result = unwrapResult(response);
      setTimeSlots([...timeSlots, result]);
      setStartHour("");
      setStartMinute("");
      setStartAmPm("AM");
      setEndHour("");
      setEndMinute("");
      setEndAmPm("PM");
      setLoadingMessage("Time slot added successfully!");
    } catch (err: any) {
      setLoadingMessage("Failed to add time slot.");
      console.error("Error adding time slot:", err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
  };

  const handleEditClick = (id: number, currentValue: string) => {
    const [startPart, endPart] = currentValue.split(" - ");
    const [startTime, startAmPm] = startPart.split(" ");
    const [startHour, startMinute] = startTime.split(":");
    const [endTime, endAmPm] = endPart.split(" ");
    const [endHour, endMinute] = endTime.split(":");

    setEditingId(id);
    setEditStartHour(parseInt(startHour, 10).toString());
    setEditStartMinute(startMinute);
    setEditStartAmPm(startAmPm);
    setEditEndHour(parseInt(endHour, 10).toString());
    setEditEndMinute(endMinute);
    setEditEndAmPm(endAmPm);
  };

  const handleSaveChanges = async () => {
    if (editingId === null) return;

    if (
      !validateTimeInputs(editStartHour, editStartMinute, editStartAmPm) ||
      !validateTimeInputs(editEndHour, editEndMinute, editEndAmPm)
    ) {
      setLoadingMessage("Please enter valid time values");
      setIsLoading(true);
      setTimeout(() => setIsLoading(false), 2000);
      return;
    }

    const startTime = formatTimeSlot(editStartHour, editStartMinute, editStartAmPm);
    const endTime = formatTimeSlot(editEndHour, editEndMinute, editEndAmPm);
    const timeSlotString = `${startTime} - ${endTime}`;

    setIsLoading(true);
    setLoadingMessage("Saving changes...");
    
    try {
      const response = await dispatch<any>(updateTimeSlot({
        id: editingId,
        window: timeSlotString,
      }));
      const result = unwrapResult(response);
      setTimeSlots(timeSlots.map(slot => 
        slot.id === editingId ? { ...slot, window: timeSlotString } : slot
      ));
      setEditingId(null);
      setLoadingMessage("Changes saved successfully!");
    } catch (err: any) {
      setLoadingMessage("Failed to save changes.");
      console.error("Error saving changes:", err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
  };

  const handleDeleteTimeSlot = async (id: number) => {
    if (!id) return;
    setIsLoading(true);
    setLoadingMessage("Deleting time slot...");
    try {
      await dispatch<any>(deleteTimeSlot(id));
      setTimeSlots(prevSlots => prevSlots.filter(slot => slot.id !== id)); 
      setLoadingMessage("Time slot deleted successfully!");
    } catch (err: any) {
      setLoadingMessage("Failed to delete time slot.");
      console.error("Error deleting time slot:", err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const renderTimeInputs = (isEditMode: boolean) => {
    const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

    const state = isEditMode ? {
      startHour: editStartHour,
      startMinute: editStartMinute,
      startAmPm: editStartAmPm,
      endHour: editEndHour,
      endMinute: editEndMinute,
      endAmPm: editEndAmPm,
      setStartHour: setEditStartHour,
      setStartMinute: setEditStartMinute,
      setStartAmPm: setEditStartAmPm,
      setEndHour: setEditEndHour,
      setEndMinute: setEditEndMinute,
      setEndAmPm: setEditEndAmPm,
    } : {
      startHour,
      startMinute,
      startAmPm,
      endHour,
      endMinute,
      endAmPm,
      setStartHour: setStartHour,
      setStartMinute: setStartMinute,
      setStartAmPm: setStartAmPm,
      setEndHour: setEndHour,
      setEndMinute: setEndMinute,
      setEndAmPm: setEndAmPm,
    };

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle1" gutterBottom>
            Start Time
          </Typography>
          <Box display="flex" gap={1}>
            <FormControl fullWidth>
              <InputLabel>Hour</InputLabel>
              <Select
                value={state.startHour}
                onChange={(e) => state.setStartHour(e.target.value)}
                label="Hour"
              >
                {hourOptions.map((hour) => (
                  <MenuItem key={`start-hour-${hour}`} value={hour.toString()}>
                    {hour.toString().padStart(2, "0")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Minute</InputLabel>
              <Select
                value={state.startMinute}
                onChange={(e) => state.setStartMinute(e.target.value)}
                label="Minute"
              >
                {minuteOptions.map((minute) => {
                  const minuteStr = minute.toString().padStart(2, "0");
                  return (
                    <MenuItem key={`start-minute-${minute}`} value={minuteStr}>
                      {minuteStr}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>AM/PM</InputLabel>
              <Select
                value={state.startAmPm}
                onChange={(e) => state.setStartAmPm(e.target.value)}
                label="AM/PM"
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle1" gutterBottom>
            End Time
          </Typography>
          <Box display="flex" gap={1}>
            <FormControl fullWidth>
              <InputLabel>Hour</InputLabel>
              <Select
                value={state.endHour}
                onChange={(e) => state.setEndHour(e.target.value)}
                label="Hour"
              >
                {hourOptions.map((hour) => (
                  <MenuItem key={`end-hour-${hour}`} value={hour.toString()}>
                    {hour.toString().padStart(2, "0")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Minute</InputLabel>
              <Select
                value={state.endMinute}
                onChange={(e) => state.setEndMinute(e.target.value)}
                label="Minute"
              >
                {minuteOptions.map((minute) => {
                  const minuteStr = minute.toString().padStart(2, "0");
                  return (
                    <MenuItem key={`end-minute-${minute}`} value={minuteStr}>
                      {minuteStr}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>AM/PM</InputLabel>
              <Select
                value={state.endAmPm}
                onChange={(e) => state.setEndAmPm(e.target.value)}
                label="AM/PM"
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography fontSize={16} fontWeight={500} textAlign="center" mt={1}>
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Typography sx={{ fontWeight: "bold", mb: 4, fontSize: "2rem" }}>
        Time Slots
      </Typography>

      {editingId === null ? (
        <Box sx={{ mb: 4 }}>
          {renderTimeInputs(false)}
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleAddTimeSlot}
            disabled={!startHour || !startMinute || !endHour || !endMinute}
            sx={{ mt: 2 }}
          >
            Add Time Slot
          </Button>
        </Box>
      ) : (
        <Box sx={{ mb: 4 }}>
          {renderTimeInputs(true)}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={handleSaveChanges}
              disabled={!editStartHour || !editStartMinute || !editEndHour || !editEndMinute}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      )}

      <List sx={{ width: "100%", bgcolor: "background.paper" }}>
        {timeSlots.map((slot) => (
          <React.Fragment key={slot.id}>
            <ListItem
              secondaryAction={
                editingId === slot.id ? null : (
                  <Box>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleEditClick(slot.id!, slot.window)}
                      sx={{ mr: 1 }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteTimeSlot(slot.id!)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                )
              }
            >
              <ListItemText primary={slot.window} />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default TimeSlots;